/**
 * Карточка поля v3 — вкладка «Обзор», виджеты (PR-B).
 *
 * Чистые хелперы витрин виджетов (операции, осмотры, мини-карта, рельеф).
 * Держим отдельно от компонентов, чтобы юнит-тестить без монтирования DOM —
 * тот же паттерн, что inspectionLogic.ts.
 */

/** Взять первые n элементов (для «3 последних»). null/undefined → []. */
export function latestN<T>(arr: T[] | null | undefined, n: number): T[] {
  if (!arr?.length) return [];
  return arr.slice(0, Math.max(0, n));
}

// ─── Тех. операции ──────────────────────────────────────────────────────

/** Минимальная форма OperationOut, нужная витрине (operations.py:27). */
export interface OperationLike {
  id: string;
  name: string;
  op_type_local_name?: string | null;
  crop_name?: string | null;
  planned_start_date?: string | null;
  actual_start_date?: string | null;
  planned_area_ha?: number | null;
  actual_area_ha?: number | null;
  lifecycle_state: string;
  display_status?: string | null;
}

export type OpTone = 'fact' | 'plan' | 'cancelled';

export interface OpChip {
  tone: OpTone;
  label: string;
}

/**
 * Чип статуса операции по эталону founder:
 *   - есть факт-дата или lifecycle done → зелёный «✓ факт»
 *   - отменена → нейтральный «отменено»
 *   - иначе → янтарный «план»
 */
export function operationChip(op: OperationLike): OpChip {
  if (op.lifecycle_state === 'cancelled') {
    return { tone: 'cancelled', label: 'отменено' };
  }
  if (op.actual_start_date != null || op.lifecycle_state === 'done') {
    return { tone: 'fact', label: '✓ факт' };
  }
  return { tone: 'plan', label: 'план' };
}

/** Дата операции для строки: факт-дата приоритетнее плановой. */
export function operationDate(op: OperationLike): string | null {
  return op.actual_start_date ?? op.planned_start_date ?? null;
}

/**
 * Подзаголовок строки операции: «факт · 60,8 га» / «план · Пшеница озимая».
 * Для факта показываем площадь, для плана — тип операции / культуру.
 */
export function operationSubtitle(op: OperationLike): string {
  const chip = operationChip(op);
  const parts: string[] = [];
  if (chip.tone === 'fact') {
    parts.push('факт');
    const area = op.actual_area_ha ?? op.planned_area_ha;
    if (area != null) parts.push(`${formatHa(area)} га`);
  } else if (chip.tone === 'cancelled') {
    parts.push('отменено');
  } else {
    parts.push('план');
    const extra = op.op_type_local_name ?? op.crop_name;
    if (extra) parts.push(extra);
  }
  return parts.join(' · ');
}

// ─── Форматирование ─────────────────────────────────────────────────────

/** "60,8" — площадь без единицы (единицу дописывает вызывающий). */
export function formatHa(v?: number | null): string {
  if (v == null) return '—';
  return v.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
}

/** "0,8°" — уклон в градусах, одна десятая. null → «—». */
export function formatSlope(v?: number | null): string {
  if (v == null) return '—';
  return `${v.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}°`;
}

/** "07.07" — короткая дата (день.месяц) из ISO. Битая дата → "". */
export function formatShortDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}`;
}

/** "07.07.2026" — полная числовая дата из ISO. Битая → "". */
export function formatFullDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
}

/** Русская подпись румба экспозиции (латинский код бэкенда → короткая). */
const ASPECT_SHORT: Record<string, string> = {
  N: 'С',
  NE: 'С-В',
  E: 'В',
  SE: 'Ю-В',
  S: 'Ю',
  SW: 'Ю-З',
  W: 'З',
  NW: 'С-З',
};

export function aspectShort(cardinal?: string | null): string {
  if (!cardinal) return '—';
  return ASPECT_SHORT[cardinal] ?? cardinal;
}
